import { useState, useEffect, useMemo } from 'react';
import { Notification } from '@/types/notifications-types';
import { useNotifications } from '@/contexts/notification-context';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/auth';

/**
 * Custom hook for notifications page logic - now using real database
 */
const useNotificationsPage = () => {
  const { resetUnreadCount, setUnreadCount, decrementUnreadCount } = useNotifications();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch notifications from database
  const { data: dbNotifications = [], isLoading, error } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Failed to fetch notifications:', error);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true
  });

  // Transform database notifications to frontend format
  const notifications = useMemo((): Notification[] => {
    return dbNotifications.map(dbNotification => ({
      id: parseInt(dbNotification.id), // Convert UUID to number for frontend compatibility
      title: dbNotification.title,
      message: dbNotification.message,
      type: dbNotification.type as 'info' | 'success' | 'warning' | 'error',
      date: dbNotification.created_at,
      link: dbNotification.link || undefined,
      actionLabel: dbNotification.action_label || undefined,
      read: dbNotification.is_read
    }));
  }, [dbNotifications]);

  // Return all notifications as filtered notifications (no filtering)
  const filteredNotifications = useMemo(() => {
    return notifications;
  }, [notifications]);

  // Mutation to mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user?.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Refetch notifications to update the UI
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to mark notification as read:', error);
      toast({
        title: 'Error',
        description: 'Failed to mark notification as read. Please try again.',
        variant: 'error'
      });
    }
  });

  // Mutation to delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', user?.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Refetch notifications to update the UI
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to delete notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notification. Please try again.',
        variant: 'error'
      });
    }
  });

  // Mutation to delete all notifications
  const deleteAllNotificationsMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user?.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      // Refetch notifications to update the UI
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
    onError: (error) => {
      console.error('Failed to delete all notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete notifications. Please try again.',
        variant: 'error'
      });
    }
  });

  // Handle marking a notification as read
  const handleMarkAsRead = (id: number) => {
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;
    
    // Find the corresponding database notification
    const dbNotification = dbNotifications.find(n => parseInt(n.id) === id);
    if (!dbNotification) return;
    
    markAsReadMutation.mutate(dbNotification.id);
    decrementUnreadCount();
    
    toast({
      title: 'Notification marked as read',
      description: 'The notification has been marked as read.',
      duration: 2000,
      variant: 'default'
    });
  };

  // Handle removing a notification
  const handleRemoveNotification = (id: number) => {
    const notification = notifications.find(n => n.id === id);
    if (!notification) return;
    
    // Find the corresponding database notification
    const dbNotification = dbNotifications.find(n => parseInt(n.id) === id);
    if (!dbNotification) return;
    
    deleteNotificationMutation.mutate(dbNotification.id);
    
    // Update unread count if notification was unread
    if (!notification.read) {
      decrementUnreadCount();
    }
    
    toast({
      title: 'Notification removed',
      description: 'The notification has been removed.',
      duration: 3000,
      variant: 'default'
    });
  };

  // Handle removing all notifications
  const handleRemoveAllNotifications = () => {
    const notificationCount = notifications.length;
    
    if (notificationCount === 0) return;
    
    deleteAllNotificationsMutation.mutate();
    resetUnreadCount();
    
    toast({
      title: 'All notifications deleted',
      description: `${notificationCount} notification${notificationCount !== 1 ? 's' : ''} have been deleted.`,
      duration: 3000,
      variant: 'error'
    });
  };

  // Update unread count in global state when notifications change
  useEffect(() => {
    // Count unread notifications
    const unreadCount = notifications.filter(notification => !notification.read).length;
    // Set the unread count in the context
    setUnreadCount(unreadCount);
  }, [notifications, setUnreadCount]);

  // Handle query errors
  useEffect(() => {
    if (error) {
      console.error('Notifications query error:', error);
      toast({
        title: 'Error loading notifications',
        description: 'Failed to load notifications. Please refresh the page.',
        variant: 'error'
      });
    }
  }, [error, toast]);

  return {
    notifications,
    filteredNotifications,
    handleMarkAsRead,
    handleRemoveNotification,
    handleRemoveAllNotifications,
    isLoading,
    error
  };
};

export default useNotificationsPage;
